import { ToolCallInfo } from '~/components/AiChat/AiChatContext';
import type { ChatThreadMode } from '~/components/AiChat/AiChatContext';

import { json_headers, request } from './common';

export interface ChatRequestItem {
  role: 'user';
  content: string;
  kind: 'context' | 'user';
}

export interface ChatRequestContext {
  conversationRef: string | null;
  agentId: string | null;
  agentName?: string | null;
  agentEmail?: string | null;
  agentRoles?: Array<string>;
  customerId?: string | null;
  customerName?: string | null;
  orderId?: string | null;
  department?: string | null;
  mode?: ChatThreadMode;
  page?: string | null;
  hasPriorFraudDecision?: boolean;
}

export interface CreateConversationResponse {
  conversationId: string;
}

export interface SendConversationItemsRequest {
  conversationId: string;
  context: ChatRequestContext;
  items: Array<ChatRequestItem>;
  model?: string;
}

export interface ChatResponse {
  content: string;
  toolCalls: Array<ToolCallInfo>;
  responseId: string | null;
}

interface ResponsesApiOutput {
  type: string;
  role?: string;
  content?: Array<{ type: string; text: string }>;
}

interface ResponsesApiResult {
  id: string;
  output: Array<ResponsesApiOutput>;
  toolCalls?: Array<ToolCallInfo>;
}

const DEFAULT_ADMIN_AI_API_PATH = '/api/admin-ai';

function trimTrailingSlash(value: string): string {
  return value.replace(/\/$/, '');
}

function trimLeadingSlash(value: string): string {
  return value.replace(/^\//, '');
}

function getAdminAiApiBaseUrl(): string {
  const configuredBaseUrl = window.configs?.ADMIN_AI_API_BASE_URL;

  if (typeof configuredBaseUrl === 'string' && configuredBaseUrl.trim()) {
    return trimTrailingSlash(configuredBaseUrl.trim());
  }

  const apiHost = window.configs?.API_HOST;
  if (typeof apiHost === 'string' && apiHost.trim()) {
    return `${trimTrailingSlash(apiHost.trim())}${DEFAULT_ADMIN_AI_API_PATH}`;
  }

  return DEFAULT_ADMIN_AI_API_PATH;
}

function getAdminAiApiUrl(path: string): string {
  return `${getAdminAiApiBaseUrl()}/${trimLeadingSlash(path)}`;
}

export async function createConversation(): Promise<CreateConversationResponse> {
  const result = await request(getAdminAiApiUrl('/conversations'), {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({}),
  });

  return {
    conversationId: result?.conversationId,
  };
}

export async function sendConversationItems(payload: SendConversationItemsRequest): Promise<ChatResponse> {
  const result: ResponsesApiResult = await request(getAdminAiApiUrl('/responses'), {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  const messageOutput = result.output?.find((o) => o.type === 'message' && o.role === 'assistant');
  const textContent = messageOutput?.content?.find((c) => c.type === 'output_text');

  return {
    content: textContent?.text || 'No response received.',
    toolCalls: result.toolCalls || [],
    responseId: result.id || null,
  };
}

export async function transcribeAudio(audioBlob: Blob): Promise<string> {
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');

  const result = await request(getAdminAiApiUrl('/audio/transcriptions'), {
    method: 'POST',
    body: formData,
  });

  return result?.text || '';
}
